const JsTools = require('@bm/js-tools')

const HIDDEN_INPUT_TYPE = 'hidden'
const CHECKBOX_INPUT_TYPE = 'checkbox'
const RADIO_INPUT_TYPE = 'radio'
const FORM_STATUS = {
  initiated: 'INITIATED',
  pending: 'PENDING',
  disabled: 'DISABLED',
  active: 'ACTIVE',
  valid: 'VALID',
  invalid: 'INVALID'
}
const FORM_CSS_CLASS = {
  pristine: 'pristine',
  dirty: 'dirty',
  untouched: 'untouched',
  touched: 'touched',
  invalid: 'invalid',
  valid: 'valid',
  submitted: 'submitted',
  empty: 'empty',
  filled: 'filled'
}
const FORMS = []
let CONTROLS = []

const VALIDATORS = {
  required: (value) => (!!value || value === 0),
  maxlength: (value, el) => {
    if (!value || !el) {
      return true
    }
    return value.length <= el.getAttribute('maxlength')
  },
  email: (value) => {
    if (!value) {
      return true
    }
    return /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(value)
  }
}

class FormDirectiveHelpers {
  static addCssClassToNode (node, cssClass) {
    node.classList.add(cssClass)
  }

  static removeCssClassFromNode (node, cssClass) {
    node.classList.remove(cssClass)
  }

  static setNodeValidationCssClass (node, valid) {
    if (valid) {
      FormDirectiveHelpers.removeCssClassFromNode(node, FORM_CSS_CLASS.invalid)
      FormDirectiveHelpers.addCssClassToNode(node, FORM_CSS_CLASS.valid)
    } else {
      FormDirectiveHelpers.removeCssClassFromNode(node, FORM_CSS_CLASS.valid)
      FormDirectiveHelpers.addCssClassToNode(node, FORM_CSS_CLASS.invalid)
    }
  }

  static setNodeContentsCssClass (node, filled) {
    if (filled) {
      FormDirectiveHelpers.removeCssClassFromNode(node, FORM_CSS_CLASS.empty)
      FormDirectiveHelpers.addCssClassToNode(node, FORM_CSS_CLASS.filled)
    } else {
      FormDirectiveHelpers.removeCssClassFromNode(node, FORM_CSS_CLASS.filled)
      FormDirectiveHelpers.addCssClassToNode(node, FORM_CSS_CLASS.empty)
    }
  }

  static setNodeTouchingCssClass (node, touched) {
    if (touched) {
      FormDirectiveHelpers.removeCssClassFromNode(node, FORM_CSS_CLASS.untouched)
      FormDirectiveHelpers.addCssClassToNode(node, FORM_CSS_CLASS.touched)
    } else {
      FormDirectiveHelpers.removeCssClassFromNode(node, FORM_CSS_CLASS.touched)
      FormDirectiveHelpers.addCssClassToNode(node, FORM_CSS_CLASS.untouched)
    }
  }

  static setNodePurityCssClass (node, dirty) {
    if (dirty) {
      FormDirectiveHelpers.removeCssClassFromNode(node, FORM_CSS_CLASS.pristine)
      FormDirectiveHelpers.addCssClassToNode(node, FORM_CSS_CLASS.dirty)
    } else {
      FormDirectiveHelpers.removeCssClassFromNode(node, FORM_CSS_CLASS.dirty)
      FormDirectiveHelpers.addCssClassToNode(node, FORM_CSS_CLASS.pristine)
    }
  }

  static setNodeSubmitCssClass (node, submitted) {
    if (submitted) {
      FormDirectiveHelpers.addCssClassToNode(node, FORM_CSS_CLASS.submitted)
    } else {
      FormDirectiveHelpers.removeCssClassFromNode(node, FORM_CSS_CLASS.submitted)
    }
  }

  static runValidator (validatorName, value, field, form, context) {
    const result = VALIDATORS[validatorName](value, field, form, context)
    return {
      validator: validatorName,
      valid: typeof result === 'boolean' ? result : false,
      validatorMessage: typeof result !== 'boolean' ? result : null
    }
  }

  static addFormInstanceToWindow (formInstance) {
    if (!window.formDirectives) {
      window.formDirectives = []
    }
    window.formDirectives.push(formInstance)
  }

  static removeFormInstanceFromWindow (formInstance) {
    if (!window.formDirectives) {
      return
    }
    window.formDirectives = window.formDirectives.filter(form => form !== formInstance)
  }

  static startValueWatcher (control, callback) {
    const property = 'value'
    Object.defineProperty(control, property, new Watcher())

    function Watcher () {
      let _value = control[property]
      return {
        set (value) {
          _value = value
          control.dispatchEvent(new Event('input'))
          callback()
        },
        get () {
          return _value
        }
      }
    }
  }
}

class FormDirectiveFormElement {
  constructor (formEl, formBindings, formVNode) {
    this.formEl = formEl
    this.formVNode = formVNode
    this.status = FORM_STATUS.initiated

    if (formBindings.arg) {
      this.formVNode.context[formBindings.arg] = this
    }
    if (formBindings.modifiers.debug) {
      this.debug = true
      FormDirectiveHelpers.addFormInstanceToWindow(this)
      addLog('Form directive initiated')
    }

    this.resetFormData()
    this.resetFormFlags()
    this.checkForChangesInFormControls(formVNode.children)

    this.formEl.addEventListener('submit', () => { this.setAsSubmitted() })
    this.formEl.addEventListener('change', () => { this.validateForm() })
    if (!formBindings.modifiers.change) {
      this.formEl.addEventListener('input', () => { this.validateForm() })
    }
  }

  updateVNode (formVNode) {
    this.formVNode = formVNode
    if (this.debug) {
      addLog('Form VNode updated')
    }
    this.checkForChangesInFormControls(formVNode.children)
  }

  resetFormData () {
    this.value = {}
    this.errors = {}
  }

  resetFields (skipControlValidation) {
    resetControls(this.controls)

    function resetControls (controls) {
      JsTools.each(controls, controlInstance => {
        if (controlInstance instanceof FormDirectiveControlElement) {
          controlInstance.resetControl(skipControlValidation)
        } else {
          resetControls(controlInstance)
        }
      })
    }
  }

  resetFormFlags () {
    this.valid = null
    this.invalid = null
    this.pending = false
    this.pristine = true
    this.dirty = false
    this.untouched = true
    this.touched = false
    this.status = null
    this.submitted = false
    FormDirectiveHelpers.setNodeSubmitCssClass(this.formEl, false)
    if (this.debug) {
      addLog('Form flags cleared')
    }
  }

  resetForm () {
    this.resetFormData()
    this.resetFormFlags()
    this.resetFields(true)
    this.validateForm()
  }

  setAsSubmitted () {
    this.submitted = true
    FormDirectiveHelpers.setNodeSubmitCssClass(this.formEl, true)
    if (this.debug) {
      addLog('Form submitted')
    }
    this.updateView()
  }

  checkForChangesInFormControls (vNodes) {
    this.checkControls(vNodes)
    this.validateForm()
  }

  checkControls (vnodes) {
    const controls = {}
    checkVNodes(vnodes, controls, this)
    checkControls(this.debug)
    this.controls = controls

    function checkVNodes (nodes, controlObj, formInstance) {
      JsTools.each(nodes, (vnode) => {
        if (vnode.tag === 'input' || vnode.tag === 'select' || vnode.tag === 'textarea') {
          const field = vnode.elm
          if (!field.name) {
            return
          }
          if (vnode.data.attrs['control-standalone'] === '' || vnode.data.attrs['control-standalone'] === true) {
            return
          }
          addField(vnode, controlObj, formInstance)
        } else if (vnode.children && vnode.children.length) {
          let groupName = ''
          if (vnode.data && vnode.data.attrs && vnode.data.attrs['model-group']) {
            groupName = vnode.data.attrs['model-group']
            if (!controlObj[groupName]) {
              controlObj[groupName] = {}
            }
          }
          checkVNodes(vnode.children, groupName ? controlObj[groupName] : controlObj, formInstance)
        }
      })
    }

    function addField (vnode, controlObj, formInstance) {
      const field = vnode.elm
      let dependentControl = null
      let controlInstance = CONTROLS.find(controlInstance => controlInstance.controlEl === field)
      if (controlInstance) {
        controlInstance.updateFieldControlVNode(vnode)
      } else {
        controlInstance = new FormDirectiveControlElement(vnode, formInstance)
        CONTROLS.push(controlInstance)
        if (formInstance.debug) {
          addLog('Field ' + field.name + ' added to form')
        }
      }
      if (controlObj[field.name]) {
        const currentInstance = controlObj[field.name]
        if (currentInstance.controlEl.type === RADIO_INPUT_TYPE) {
          dependentControl = currentInstance.dependentControlEl || []
          dependentControl.push(currentInstance.controlEl)
        }
      }
      controlObj[field.name] = controlInstance
      if (dependentControl) {
        controlObj[field.name].addDependentControlEl(dependentControl)
      }
    }

    function checkControls (debug) {
      CONTROLS = CONTROLS.filter(controlInstance => {
        if (!controlInstance.controlEl.form) {
          if (debug) {
            addLog('Field ' + controlInstance.controlEl.name + ' removed from form')
          }
          return false
        }
        return true
      })
    }
  }

  validateForm () {
    if (this.pending) {
      return
    }
    this.pending = true
    this.status = FORM_STATUS.pending
    const controlsResults = []
    const errorsBeforeValidation = JSON.stringify(this.errors)
    this.resetFormData()
    validateControl(this.controls, this.value, this.errors)
    clearObj(this.value)
    clearObj(this.errors)
    const errorsAfterValidation = JSON.stringify(this.errors)

    this.touched = !!controlsResults.find(control => control.touched)
    this.untouched = !this.touched
    this.dirty = !!controlsResults.find(control => control.dirty)
    this.pristine = !this.dirty
    this.invalid = !!controlsResults.find(control => !control.valid)
    this.valid = !this.invalid
    this.status = this.valid ? FORM_STATUS.valid : FORM_STATUS.invalid

    FormDirectiveHelpers.setNodeTouchingCssClass(this.formEl, this.touched)
    FormDirectiveHelpers.setNodePurityCssClass(this.formEl, this.dirty)
    FormDirectiveHelpers.setNodeValidationCssClass(this.formEl, this.valid)
    if (this.debug) {
      addLog('Form validated with result ' + this.valid)
    }
    if (errorsBeforeValidation !== errorsAfterValidation) {
      if (this.debug) {
        addLog('Form validation change')
      }
      this.updateView()
    }
    this.pending = false

    function validateControl (controls, valueObj, errorObj) {
      JsTools.each(controls, (controlInstance, fieldName) => {
        if (controlInstance instanceof FormDirectiveControlElement) {
          if (controlInstance.disabled) {
            return
          }
          controlInstance.validateControl()

          controlsResults.push({
            valid: controlInstance.valid,
            touched: controlInstance.touched,
            dirty: controlInstance.dirty
          })

          if (!controlInstance.standalone) {
            valueObj[fieldName] = controlInstance.value
          }
          if (controlInstance.invalid) {
            errorObj[fieldName] = controlInstance.errors
          }
        } else {
          if (!valueObj[fieldName]) {
            valueObj[fieldName] = {}
          }
          if (!errorObj[fieldName]) {
            errorObj[fieldName] = {}
          }
          validateControl(controlInstance, valueObj[fieldName], errorObj[fieldName])
        }
      })
    }
    function clearObj (obj) {
      JsTools.each(obj, (value, key) => {
        if (!value || typeof value !== 'object') {
          return
        }
        if (!Object.keys(value).length) {
          delete obj[key]
          return
        }
        clearObj(value)
      })
    }
  }

  updateView () {
    this.formVNode.context.$forceUpdate()
  }
}

class FormDirectiveControlElement {
  constructor (controlVNode, formInstance) {
    this.controlEl = controlVNode.elm
    this.controlVNode = controlVNode
    this.root = formInstance

    // if (this.controlEl.type === HIDDEN_INPUT_TYPE) {
    //   const callback = () => {
    //     this.root.validateForm()
    //   }
    //   FormDirectiveHelpers.startValueWatcher(this.controlEl, callback)
    //   if (this.root.debug) {
    //     addLog('Control' + this.controlEl.name + ' is hidden, adding watcher for value changes')
    //   }
    // }

    this.resetControlFlags()

    this.value = null
    this.errors = null
    this.status = FORM_STATUS.initiated

    this.checkControlActivity()
    this.getControlValidators()

    this.controlEl.addEventListener('blur', () => {
      if (this.touched) {
        return
      }
      this.setAsTouched()
    })
  }

  addDependentControlEl (dependentControlEl) {
    if (this.controlEl.type !== RADIO_INPUT_TYPE) {
      return
    }
    this.dependentControlEl = dependentControlEl
  }

  updateFieldControlVNode (controlVNode) {
    this.controlVNode = controlVNode
    this.checkControlActivity()
    this.getControlValidators()
  }

  resetControlValue () {
    this.setValue(null)
    if (this.root.debug) {
      addLog('Field ' + this.controlEl.name + ' cleared')
    }
  }

  resetControlFlags () {
    this.standalone = null
    this.valid = null
    this.invalid = null
    this.pending = null
    this.pristine = true
    this.dirty = false
    this.untouched = true
    this.touched = false
    this.disabled = null
    this.status = null
    if (this.root.debug) {
      addLog('Field ' + this.controlEl.name + ' flags cleared')
    }
  }

  resetControl (skipValidation) {
    this.resetControlValue()
    this.resetControlFlags()
    if (!skipValidation) {
      this.validateControl()
    }
  }

  checkControlActivity () {
    const controlData = this.controlVNode.data
    this.standalone = controlData.attrs['model-standalone'] === '' || controlData.attrs['model-standalone'] === true
    this.disabled = controlData.attrs.disabled === '' || !!controlData.attrs.disabled
    this.status = this.disabled ? FORM_STATUS.disabled : FORM_STATUS.active
  }

  getControlValidators () {
    const validators = []
    const controlData = this.controlVNode.data
    const controlDomAttrs = Object.keys(controlData.attrs).filter(attrName => typeof controlData.attrs[attrName] !== 'boolean' || controlData.attrs[attrName])
    const controlDirectives = controlData.directives ? controlData.directives.map(directive => directive.name) : []
    const controlAttrs = [...controlDomAttrs, ...controlDirectives]
    controlAttrs.forEach(attribute => {
      if (VALIDATORS[attribute]) {
        validators.push(attribute)
      }
    })
    this.validators = validators
  }

  validateControl () {
    if (this.disabled || this.pending) {
      return
    }
    this.pending = true
    this.status = FORM_STATUS.pending

    const validationResults = []
    const controlContext = this.controlVNode.context
    const controlData = this.controlVNode.data
    const controlModelDirective = controlData.directives ? controlData.directives.find(directive => directive.name === 'model') : null
    const controlBinding = controlModelDirective ? controlModelDirective.expression : null
    let controlValue = this.controlEl.value

    if (this.controlEl.type === RADIO_INPUT_TYPE && !this.controlEl.checked && this.dependentControlEl) {
      const checkedControl = this.dependentControlEl.find(control => control.checked)
      controlValue = checkedControl ? checkedControl.value : null
    }

    if (this.controlEl.type === CHECKBOX_INPUT_TYPE) {
      controlValue = this.controlEl.checked
    }

    // getting value from context
    if (controlBinding) {
      const bindingArray = controlBinding.split('.')
      let context = controlContext
      bindingArray.forEach(key => {
        context = context[key]
        controlValue = context
      })
    }

    if (this.valid !== null && this.value === controlValue && Object.keys(this.errors || {}).find(key => !this.validators.includes(key))) {
      this.status = this.valid ? FORM_STATUS.valid : FORM_STATUS.invalid
      this.pending = false
      return
    }

    this.value = controlValue
    this.errors = null

    // validating field
    this.validators.forEach(validatorName => {
      const result = FormDirectiveHelpers.runValidator(validatorName, controlValue, this.controlEl, this.controlEl.form, this.root.formVNode.context)
      validationResults.push(result)
      if (this.root.debug) {
        addLog('Validator ' + validatorName + ' returns ' + result.valid + ' with value ' + controlValue + ' on field ' + this.controlEl.name)
      }
    })

    // setting valid
    const isControlValid = !validationResults.find(result => !result.valid)
    this.valid = isControlValid
    this.invalid = !isControlValid
    this.status = isControlValid ? FORM_STATUS.valid : FORM_STATUS.invalid
    FormDirectiveHelpers.setNodeValidationCssClass(this.controlEl, isControlValid)

    if (!isControlValid) {
      const errorObj = {}
      validationResults.filter(result => !result.valid).forEach(result => {
        errorObj[result.validatorMessage || result.validator] = true
      })
      this.setErrors(errorObj)
    }

    // setting empty
    FormDirectiveHelpers.setNodeContentsCssClass(this.controlEl, !!controlValue)

    // setting touched
    FormDirectiveHelpers.setNodeTouchingCssClass(this.controlEl, this.touched)

    // setting dirty
    if (!this.dirty && controlValue) {
      this.dirty = true
      this.pristine = false
      if (this.root.debug) {
        addLog('Field ' + this.controlEl.name + ' changed to dirty')
      }
    }
    FormDirectiveHelpers.setNodePurityCssClass(this.controlEl, this.dirty)

    this.pending = false
    if (this.root.debug) {
      addLog('Field ' + this.controlEl.name + ' validated with result ' + this.valid)
    }
  }

  setValue (value) {
    if (this.controlEl.type === RADIO_INPUT_TYPE) {
      this.controlEl.checked = this.controlEl.value === value
      if (this.dependentControlEl) {
        this.dependentControlEl.forEach(control => { control.checked = control.value === value })
      }
    } else if (this.controlEl.type === CHECKBOX_INPUT_TYPE) {
      this.controlEl.checked = !!value
    } else {
      this.controlEl.value = value
      this.controlEl.dispatchEvent(new Event('input'))
    }
    this.root.validateForm()
  }

  setAsTouched () {
    if (this.touched) {
      return
    }
    this.touched = true
    this.untouched = false
    FormDirectiveHelpers.setNodeTouchingCssClass(this.controlEl, this.touched)
    this.root.updateView()
    if (this.root.debug) {
      addLog('Field ' + this.controlEl.name + ' changed to touched')
    }
  }

  setErrors (errorObj) {
    if (!errorObj || typeof errorObj !== 'object') {
      return
    }
    this.invalid = true
    this.valid = false
    this.errors = Object.assign({}, errorObj)
    FormDirectiveHelpers.setNodeValidationCssClass(this.controlEl, this.valid)
    if (this.root.debug) {
      JsTools.each(errorObj, (value, key) => {
        addLog('Error ' + key + '  added to field ' + this.controlEl.name)
      })
    }
    if (!this.root.pending) {
      this.root.updateView()
    }
  }
}

function addLog (log) {
  console.log('[FormDirective] ' + log)
}

function createFormClass (formEl, formBindings, formVNode) {
  FORMS.push(new FormDirectiveFormElement(formEl, formBindings, formVNode))
}

function findFormInstance (el) {
  return FORMS.find(instance => instance.formEl === el)
}

function updateFormFields (el, vnode) {
  const result = findFormInstance(el)
  result.updateVNode(vnode)
}

function removeFormInstance (el) {
  const result = findFormInstance(el)
  JsTools.each(result.controls, controlInstance => {
    CONTROLS.splice(CONTROLS.indexOf(controlInstance), 1)
  })
  FORMS.splice(FORMS.indexOf(result), 1)
  if (result.debug) {
    FormDirectiveHelpers.removeFormInstanceFromWindow(result)
  }
}

const VueFormDirective = {
  bind: function (el, binding, vnode) {
    createFormClass(el, binding, vnode)
  },
  componentUpdated (el, binding, vnode) {
    updateFormFields(el, vnode)
  },
  unbind (el) {
    removeFormInstance(el)
  }
}

const install = function (Vue, options) {
  Vue.directive('form', VueFormDirective)
  if (options && options.validators) {
    Object.assign(VALIDATORS, options.validators)
  }

  Vue.prototype.$vueForm = {
    forms: FORMS,
    controls: CONTROLS,
    validators: Object.keys(VALIDATORS)
  }
}

module.exports = {
  install: install
}
